import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { Fragment, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { LearningOpportunity } from '~/shared/data-contract/learning-opportunities';
import type { UserJobRelevantSkills } from '~/shared/data-contract/user-job';
import { useLearningOpportunitiesOptionsQuery } from '~/shared/query-hooks/learning-opportunities';

import { useLearningOpportunityRecommendationsQuery } from '../query-hooks/users/learning-opportunities';
import { LearningRecommendationSection } from './LearningRecommendationSection';

const learningOpportunityRecommendationLimit = 5;

type RelevantAvailableCoursesSectionProps = {
    readonly jobId: string;
    readonly missingSkills: UserJobRelevantSkills['missingSkills'];
    readonly onLearningOpportunityClick?: (learningOpportunity: LearningOpportunity) => void;
};

export const RelevantAvailableLearningOpportunitiesSection: FunctionComponent<RelevantAvailableCoursesSectionProps> = (
    properties,
) => {
    const { jobId, missingSkills, onLearningOpportunityClick } = properties;
    const { actions, applicationState } = useContext(ApplicationContext);
    const { myUserProfile } = applicationState;
    const userId = myUserProfile.get('id').unwrapOr('');

    const { data: learningOpportunitiesOptions } = useLearningOpportunitiesOptionsQuery();
    const enabled = Boolean(learningOpportunitiesOptions?.canGetRecommendedLearningOpportunities);

    const { data: learningRecommendations } = useLearningOpportunityRecommendationsQuery(
        userId,
        jobId,
        enabled,
        learningOpportunityRecommendationLimit,
    );

    const onViewAllClick = (): void => {
        const skillIds = missingSkills.map((skill) => skill.id);
        actions.navigateTo(`/learning-opportunity-recommendations?skillIds=${String(skillIds)}`);
    };

    const defaultLearningRecommendations = learningRecommendations ?? [];

    return (
        <Fragment>
            {is.array(learningRecommendations) && is.nonEmptyArray(learningRecommendations) && (
                <LearningRecommendationSection
                    learningRecommendations={defaultLearningRecommendations}
                    onLearningOpportunityClick={onLearningOpportunityClick}
                    onViewAllClick={onViewAllClick}
                />
            )}
        </Fragment>
    );
};
